// Shared auth shapes — the server profile returned from /api/auth/session
// after Firebase sign-in. Add fields here as the screens that consume them
// migrate (handles, partyAccess, etc).

/** Four buckets, role-gated nav.
 *
 * - `pending` — newly registered, awaiting an admin approval. Sees the
 *               PendingApprovalScreen instead of the regular shell.
 * - `admin`   — full nav including the Admin tab.
 * - `manager` — like admin but no Admin tab.
 * - `user`    — like manager. In Replies, sees only comments on their
 *               own pages, AND only when `commentTrackingEnabled=true`.
 *
 * `gray_user` was a processor-era fifth bucket. transcriber-api never
 * issued it (it rejects the value with 400 invalid_role) and F-01
 * dropped it from the type. Anything an API still sends is folded into
 * 'user' by `normalizeRole` below, so a legacy row cannot land the shell
 * on an undefined ROLE_ROUTES entry.
 */
export type UserRole = 'pending' | 'admin' | 'manager' | 'user';

export const USER_ROLES: readonly UserRole[] = ['pending', 'admin', 'manager', 'user'];

/**
 * Folds whatever the server sent into a role this app knows. Unknown
 * values — 'gray_user', a typo, a role added by a newer backend —
 * become 'user': the least-privileged signed-in bucket, never an
 * accidental promotion. Absent/empty also becomes 'user', matching the
 * degraded-session fallback in the web app's session module.
 */
export function normalizeRole(raw: unknown): UserRole {
  return typeof raw === 'string' && (USER_ROLES as readonly string[]).includes(raw)
    ? (raw as UserRole)
    : 'user';
}

export interface UserHandle {
  platform: string; // 'facebook' | 'instagram' | 'twitter' | 'tiktok' | 'youtube' | …
  sourceId: string;
}

export interface SessionUser {
  _id: string;
  /** Sign-in e-mail (accounts are created in the Firebase Console).
   * Optional until transcriber-api R-06 ships the field. */
  email?: string | null;
  /** Legacy phone-auth identity; may be absent or null for e-mail accounts. */
  phoneNumber?: string;
  /** Server-provided display name. Empty until the user sets one. */
  displayName?: string;
  role: UserRole;
  /**
   * Social handles claimed by this user. Monitoring-era scoping signal
   * (which actors' posts are "yours"); consumed today only by the
   * Replies inbox, which filters suggestions to the user's own pages.
   */
  handles?: UserHandle[];
  /** Replies inbox gate for `role==='user'`. Admin/manager always see
   * the inbox; user sees it only when this is true. */
  commentTrackingEnabled?: boolean;
  /** Language picked at onboarding — used by the PendingApprovalScreen
   * when the rest of the app shell isn't mounted yet. */
  language?: string;
  /** ISO timestamp set when an admin moves the user out of `pending`. */
  approvedAt?: string;
  /**
   * `true` when /api/auth/session failed but Firebase sign-in succeeded —
   * the legacy auth-session.jsx shipped this fallback. UI should treat the
   * user as signed-in but with reduced privileges (default role: 'user').
   */
  degraded?: boolean;
}

export type AuthStatus = 'loading' | 'signed-out' | 'ready';

export interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
}
